import React, { useCallback, useEffect, useState } from 'react';
import { FaCogs, FaEdit, FaSave, FaTimes, FaSpinner, FaDownload } from 'react-icons/fa';
import boletasPlanillaApi from '../../services/api/boletasPlanillaApi';
import useRrhhProyecto from '../../hooks/useRrhhProyecto';
import RrhhProyectoSelector from '../../components/rrhh/RrhhProyectoSelector';

const HomologacionBoletas = () => {
  const {
    isCorporativo,
    proyectoId,
    setProyectoId,
    proyectos,
    loadingProyectos,
    needsProyectoSelection,
  } = useRrhhProyecto();

  const [campos, setCampos] = useState([]);
  const [plantilla, setPlantilla] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [mapeos, setMapeos] = useState({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!proyectoId) return;
    setLoading(true);
    try {
      const [rCampos, rPlantilla] = await Promise.all([
        boletasPlanillaApi.getCampos(),
        boletasPlanillaApi.getPlantillaSistema(proyectoId),
      ]);
      setCampos(rCampos.data?.data || []);
      setPlantilla(rPlantilla.data?.data || null);
    } catch (e) {
      setPlantilla(null);
      setMsg({
        type: 'error',
        text: e.response?.data?.detail
          || 'No hay plantilla en la base de datos. Ejecute insertar_plantilla_estandar_boletas.sql',
      });
    } finally {
      setLoading(false);
    }
  }, [proyectoId]);

  useEffect(() => { load(); }, [load]);

  const cargarFormDesdePlantilla = (data) => {
    setForm({
      nombre: data.nombre || '',
      descripcion: data.descripcion || '',
      empresa_ruc: data.empresa_ruc || '',
      empresa_razon_social: data.empresa_razon_social || '',
      empresa_rubro: data.empresa_rubro || '',
      empresa_direccion: data.empresa_direccion || '',
      lugar_emision: data.lugar_emision || '',
      activo: !!data.activo,
    });
    const map = {};
    (campos || []).forEach((c) => { map[c.codigo] = ''; });
    (data.mapeos || []).forEach((m) => { map[m.campo_codigo] = m.columna_excel || ''; });
    setMapeos(map);
  };

  const openEdit = () => {
    if (!plantilla) return;
    cargarFormDesdePlantilla(plantilla);
    setShowModal(true);
  };

  const handleDescargarModelo = async () => {
    if (!plantilla?.id) {
      setMsg({ type: 'error', text: 'No hay plantilla configurada en la base de datos' });
      return;
    }
    setDownloading(true);
    try {
      const nombre = (plantilla.nombre || 'plantilla_boleta').replace(/\s+/g, '_');
      await boletasPlanillaApi.downloadModeloExcel(plantilla.id, `${nombre}.xlsx`);
    } catch {
      setMsg({ type: 'error', text: 'No se pudo descargar el modelo Excel' });
    } finally {
      setDownloading(false);
    }
  };

  const handleSave = async () => {
    if (!plantilla?.id) return;
    if (!form.nombre.trim()) {
      setMsg({ type: 'error', text: 'El nombre es obligatorio' });
      return;
    }
    const mapeosList = Object.entries(mapeos)
      .filter(([, col]) => col && col.trim())
      .map(([campo_codigo, columna_excel]) => ({ campo_codigo, columna_excel: columna_excel.trim() }));

    if (!mapeosList.some((m) => m.campo_codigo === 'dni')) {
      setMsg({ type: 'error', text: 'Debe mapear al menos la columna del DNI' });
      return;
    }

    setSaving(true);
    try {
      await boletasPlanillaApi.updateHomologacion(plantilla.id, { ...form, mapeos: mapeosList });
      setShowModal(false);
      setMsg({ type: 'success', text: 'Plantilla actualizada' });
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Error al guardar' });
    } finally {
      setSaving(false);
    }
  };

  const secciones = [...new Set(campos.map((c) => c.seccion))];

  if (needsProyectoSelection) {
    return (
      <div className="dh-reporte-bg p-4">
        <RrhhProyectoSelector
          isCorporativo={isCorporativo}
          proyectos={proyectos}
          proyectoId={proyectoId}
          onChange={setProyectoId}
          loading={loadingProyectos}
        />
        <div className="dh-alert dh-alert-info">Seleccione un proyecto para configurar homologaciones.</div>
      </div>
    );
  }

  return (
    <div className="dh-reporte-bg" style={{ minHeight: '100vh', padding: '1.5rem' }}>
      <style>{`
        .dh-reporte-bg { background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%); }
        .dh-main-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #fff; padding: 1.5rem; border-radius: 12px; margin-bottom: 1.5rem; }
        .dh-main-title { margin: 0; font-size: 1.5rem; font-weight: 700; }
        .dh-main-subtitle { margin: 0.25rem 0 0; opacity: 0.9; font-size: 0.95rem; }
        .dh-card { background: #fff; border-radius: 12px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); margin-bottom: 1rem; }
        .dh-card-header { padding: 1rem 1.25rem; border-bottom: 1px solid #eee; font-weight: 600; }
        .dh-card-header-primary { background: linear-gradient(135deg, #667eea15, #764ba215); }
        .btn-dh-primary { background: linear-gradient(135deg, #11998e, #38ef7d); color: #fff; border: none; padding: 0.5rem 1rem; border-radius: 8px; font-weight: 600; }
        .btn-dh-secondary { background: #6c757d; color: #fff; border: none; padding: 0.5rem 1rem; border-radius: 8px; }
        .dh-form-label { font-weight: 600; font-size: 0.85rem; margin-bottom: 0.25rem; }
        .dh-form-control { border-radius: 8px; border: 1px solid #ddd; padding: 0.45rem 0.65rem; width: 100%; }
        .dh-form-control:focus { border-color: #667eea; outline: none; box-shadow: 0 0 0 2px #667eea33; }
        .dh-alert { padding: 0.75rem 1rem; border-radius: 8px; margin-bottom: 1rem; }
        .dh-alert-info { background: #e7f3ff; color: #0c5460; border: 1px solid #bee5eb; }
      `}</style>

      <RrhhProyectoSelector
        isCorporativo={isCorporativo}
        proyectos={proyectos}
        proyectoId={proyectoId}
        onChange={setProyectoId}
        loading={loadingProyectos}
      />

      {msg && (
        <div className={`dh-alert ${msg.type === 'error' ? '' : 'dh-alert-info'}`}
          style={msg.type === 'error' ? { background: '#fee', color: '#c00', border: '1px solid #fcc' } : {}}>
          {msg.text}
          <button type="button" className="btn btn-sm float-end" onClick={() => setMsg(null)}>×</button>
        </div>
      )}

      <div className="dh-main-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h1 className="dh-main-title"><FaCogs className="me-2" />Homologación Boletas</h1>
          <p className="dh-main-subtitle">Plantilla de mapeo Excel → campos de boleta (desde base de datos)</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-dh-secondary"
            onClick={handleDescargarModelo}
            disabled={!plantilla?.id || downloading}
          >
            {downloading ? <FaSpinner className="fa-spin me-1" /> : <FaDownload className="me-1" />}
            Descargar modelo Excel
          </button>
          <button type="button" className="btn-dh-primary" onClick={openEdit} disabled={!plantilla?.id}>
            <FaEdit className="me-1" /> Editar plantilla
          </button>
        </div>
      </div>

      <div className="dh-card">
        <div className="dh-card-header dh-card-header-primary">Plantilla del proyecto</div>
        <div className="p-3">
          {loading ? <FaSpinner className="fa-spin" /> : !plantilla ? (
            <p className="text-muted mb-0">
              No hay plantilla registrada. Ejecute el script SQL{' '}
              <code>insertar_plantilla_estandar_boletas.sql</code>.
            </p>
          ) : (
            <>
              <p className="mb-2"><strong>{plantilla.nombre}</strong></p>
              <p className="text-muted small mb-2">{plantilla.descripcion}</p>
              <div className="row g-2 small">
                <div className="col-md-4"><strong>RUC:</strong> {plantilla.empresa_ruc || '—'}</div>
                <div className="col-md-8"><strong>Empresa:</strong> {plantilla.empresa_razon_social || '—'}</div>
                <div className="col-md-12"><strong>Columnas mapeadas:</strong> {(plantilla.mapeos || []).length}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {showModal && plantilla && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar plantilla de homologación</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-2 mb-3">
                  {['nombre', 'descripcion', 'empresa_ruc', 'empresa_razon_social', 'empresa_rubro', 'empresa_direccion', 'lugar_emision'].map((f) => (
                    <div className="col-md-6" key={f}>
                      <label className="dh-form-label">{f.replace(/_/g, ' ')}</label>
                      <input className="dh-form-control" value={form[f] || ''} onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
                    </div>
                  ))}
                </div>
                <div className="dh-alert dh-alert-info mb-3">
                  Los nombres de columna se leen y guardan en{' '}
                  <strong>boletas_homologacion_mapeo</strong>. El Excel descargado usa esos mismos encabezados.
                </div>
                {secciones.map((sec) => (
                  <div key={sec} className="mb-3">
                    <h6 className="text-uppercase text-muted">{sec}</h6>
                    <div className="row g-2">
                      {campos.filter((c) => c.seccion === sec).map((c) => (
                        <div className="col-md-6" key={c.codigo}>
                          <label className="dh-form-label">
                            {c.etiqueta_boleta}
                            {c.obligatorio ? ' *' : ''}
                            <small className="text-muted ms-1">({c.codigo})</small>
                          </label>
                          <input
                            className="dh-form-control"
                            placeholder="Nombre columna Excel"
                            value={mapeos[c.codigo] || ''}
                            onChange={(e) => setMapeos({ ...mapeos, [c.codigo]: e.target.value })}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-dh-secondary" onClick={() => setShowModal(false)}><FaTimes /> Cancelar</button>
                <button type="button" className="btn-dh-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <FaSpinner className="fa-spin" /> : <FaSave />} Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomologacionBoletas;
